export const TRADE_CONFIG_KEY = 'KITE_TRADE_CONFIGURATION';

export const DEFAULT_TRADE_CONFIG = {
  target_count: 4,
  target_percentages: [25, 50, 75, 100],
  trailing_stoploss_mode: 'ENTRY_THEN_PREVIOUS_TARGET',
  stoploss_movement_plan: [],
};

export const TRAILING_STOPLOSS_MODES = [
  {
    value: 'ENTRY_THEN_PREVIOUS_TARGET',
    label: 'Entry, then previous target',
    description: 'Move SL to entry on the first configured target, then use the previous target price for later targets.',
  },
  {
    value: 'PREVIOUS_TARGET_ONLY',
    label: 'Previous target only',
    description: 'Keep the current SL on the first configured target, then use the previous target price for later targets.',
  },
  {
    value: 'LAST_HIT_TARGET',
    label: 'Last hit target',
    description: 'Move SL directly to the exact target price that just hit.',
  },
];

export const STOPLOSS_MOVEMENT_ACTIONS = [
  {
    value: 'KEEP',
    label: 'Keep current SL',
    description: 'Do not move SL when this target hits.',
  },
  {
    value: 'ENTRY_PRICE',
    label: 'Move to entry',
    description: 'Move SL to the entry price after this target hits.',
  },
  {
    value: 'PREVIOUS_TARGET',
    label: 'Move to previous target',
    description: 'Move SL to the previous target price after this target hits.',
  },
  {
    value: 'HIT_TARGET',
    label: 'Move to hit target',
    description: 'Move SL to the price of the target that just hit.',
  },
];

function normalizePercentages(values, count) {
  const parsed = Array.isArray(values) ? values.map(Number) : [];
  const fallback = Math.floor(100 / count);
  const percentages = Array.from({ length: count }, (_, index) => {
    const candidate = Number.isFinite(parsed[index]) ? parsed[index] : fallback;
    return Math.min(100, Math.max(0, candidate));
  });
  if (count === 1) return [100];
  const earlierTotal = percentages.slice(0, -1).reduce((sum, value) => sum + value, 0);
  percentages[count - 1] = Math.max(0, 100 - earlierTotal);
  return percentages;
}

function defaultStoplossMovementPlan(count) {
  return Array.from({ length: count }, (_, index) => (index === 0 ? 'ENTRY_PRICE' : 'PREVIOUS_TARGET'));
}

function normalizeStoplossMovementPlan(values, count) {
  const allowed = new Set(STOPLOSS_MOVEMENT_ACTIONS.map((action) => action.value));
  const parsed = Array.isArray(values) ? values.map((value) => String(value || '').toUpperCase()) : [];
  const fallback = defaultStoplossMovementPlan(count);
  return Array.from({ length: count }, (_, index) => (
    allowed.has(parsed[index]) ? parsed[index] : fallback[index]
  ));
}

export function normalizeTradeConfig(config = {}) {
  const targetCount = Math.min(10, Math.max(1, Math.round(Number(config.target_count) || DEFAULT_TRADE_CONFIG.target_count)));
  const stoplossMovementPlanCount = Math.max(
    targetCount,
    Math.round(Number(config.stoploss_movement_plan_count) || 0),
  );
  const trailingMode = TRAILING_STOPLOSS_MODES.some((mode) => mode.value === config.trailing_stoploss_mode)
    ? config.trailing_stoploss_mode
    : DEFAULT_TRADE_CONFIG.trailing_stoploss_mode;
  return {
    target_count: targetCount,
    target_percentages: normalizePercentages(config.target_percentages, targetCount),
    trailing_stoploss_mode: trailingMode,
    stoploss_movement_plan_count: stoplossMovementPlanCount,
    stoploss_movement_plan: normalizeStoplossMovementPlan(config.stoploss_movement_plan, stoplossMovementPlanCount),
  };
}

export function loadTradeConfig() {
  try {
    const stored = JSON.parse(localStorage.getItem(TRADE_CONFIG_KEY) || 'null');
    return normalizeTradeConfig(stored || DEFAULT_TRADE_CONFIG);
  } catch (error) {
    return { ...DEFAULT_TRADE_CONFIG };
  }
}

export function saveTradeConfig(config) {
  const normalized = normalizeTradeConfig(config);
  localStorage.setItem(TRADE_CONFIG_KEY, JSON.stringify(normalized));
  return normalized;
}

export function allocateTargetQuantities(totalQuantity, percentages) {
  const total = Math.max(0, Math.floor(Math.abs(Number(totalQuantity) || 0)));
  let allocated = 0;
  return percentages.map((percentage, index) => {
    if (index === percentages.length - 1) {
      return Math.max(0, total - allocated);
    }
    const requested = Math.max(0, Math.round(total * (Number(percentage) / 100)));
    const quantity = Math.min(requested, Math.max(0, total - allocated));
    allocated += quantity;
    return quantity;
  });
}
